"use client";
import { getUser, getActiveRole } from "@/lib/session";
import {
  createContext,
  useState,
  ReactNode,
  useContext,
  useEffect,
} from "react";
import { usePathname } from "next/navigation";

import api from "@/services/api";
import { UserDetails } from "./data.types";

const AUTH_BOOTSTRAP_SKIP_PREFIXES = [
  "/guest",
  "/linkExpired",
  "/userNotFound",
  "/userNotRegistered",
  "/faq",
  "/terms-and-condition",
  "/terms-and-conditions",
];

const shouldSkipAuthBootstrap = (pathname: string | null) => {
  if (!pathname) {
    return false;
  }

  return AUTH_BOOTSTRAP_SKIP_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
};

// Define the shape of the context data
interface AuthContextType {
  correctUserName: string;
  setCorrectUserName: (username: string) => void;
  correctPassword: string;
  setCorrectPassword: (password: string) => void;
  correctEmail: string;
  setCorrectEmail: (email: string) => void;
  correctPhone: string;
  setCorrectPhone: (phone: string) => void;
  userAuthenticated: boolean;
  setUserAuthenticated: (userAuthenticated: boolean) => void;
  isAuthReady: boolean;
  userRole: string;
  setUserRole: (userRole: string) => void;
  user: any;
  setUser: (user: any) => void;
  userDetails: UserDetails | null;
  setUserDetails: (userDetails: UserDetails | null) => void;
}

// Create the context with an initial value of undefined
const Context = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  const userInfo = getUser();
  const activeRole = getActiveRole() || userInfo?.roles?.[0];
  const [correctUserName, setCorrectUserName] = useState(userInfo?.username);
  const [correctPassword, setCorrectPassword] = useState(userInfo?.password);
  const [correctEmail, setCorrectEmail] = useState(userInfo?.email);
  const [correctPhone, setCorrectPhone] = useState(userInfo?.phone);
  const [userRole, setUserRole] = useState(activeRole);
  const [user, setUser] = useState(userInfo);
  const [userAuthenticated, setUserAuthenticated] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);

  useEffect(() => {
    if (shouldSkipAuthBootstrap(pathname)) {
      setIsAuthReady(true);
      return;
    }

    let cancelled = false;

    api
      .fetchToken()
      .then((token) => {
        if (cancelled || !token) {
          return;
        }

        setUserAuthenticated(true);
        setUserDetails(api.getUserData() as UserDetails | null);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setUserAuthenticated(false);
        setUserDetails(null);
      })
      .finally(() => {
        if (!cancelled) {
          setIsAuthReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return (
    <Context.Provider
      value={{
        correctUserName,
        setCorrectUserName,
        correctPassword,
        setCorrectPassword,
        correctEmail,
        setCorrectEmail,
        correctPhone,
        setCorrectPhone,
        userAuthenticated,
        setUserAuthenticated,
        isAuthReady,
        userRole,
        setUserRole,
        user,
        setUser,
        setUserDetails,
        userDetails,
      }}
    >
      {children}
    </Context.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
